import { useEffect, useMemo, useRef } from "react";
import { Animated, Easing, ViewStyle } from "react-native";
import { View } from "@src/components/View";
import { useUnistyles } from "react-native-unistyles";

type TypingAnimationProps = {
  dotSize?: number;
  dotColor?: string;
  style?: ViewStyle;
};

const DOT_COUNT = 3;
const BOUNCE_HEIGHT = 4;
const ANIMATION_DURATION = 450;
const STAGGER_DELAY = 120;

export function TypingAnimation({ dotSize = 8, dotColor, style }: TypingAnimationProps) {
  const { theme } = useUnistyles();
  const resolvedDotColor = dotColor ?? theme.colors.text;
  const dotAnimations = useMemo(() => Array.from({ length: DOT_COUNT }, () => new Animated.Value(0)), []);
  const animationLoops = useRef<Animated.CompositeAnimation[]>([]);

  useEffect(() => {
    animationLoops.current = dotAnimations.map((dotAnimation, index) => {
      const loop = Animated.loop(
        Animated.sequence([
          Animated.delay(index * STAGGER_DELAY),
          Animated.timing(dotAnimation, {
            toValue: -BOUNCE_HEIGHT,
            duration: ANIMATION_DURATION / 2,
            useNativeDriver: true,
            easing: Easing.out(Easing.quad),
          }),
          Animated.timing(dotAnimation, {
            toValue: 0,
            duration: ANIMATION_DURATION / 2,
            useNativeDriver: true,
            easing: Easing.in(Easing.quad),
          }),
          Animated.delay((DOT_COUNT - index) * STAGGER_DELAY),
        ]),
      );
      loop.start();
      return loop;
    });

    return () => {
      animationLoops.current.forEach((loop) => loop.stop());
    };
  }, [dotAnimations]);

  return (
    <View flexDirection="row" alignItems="flex-end" gap="sm" style={style}>
      {dotAnimations.map((dotAnimation, index) => (
        <Animated.View
          key={index}
          style={{
            width: dotSize,
            height: dotSize,
            borderRadius: dotSize / 2,
            backgroundColor: resolvedDotColor,
            transform: [{ translateY: dotAnimation }],
          }}
        />
      ))}
    </View>
  );
}
